import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || `http://${window.location.hostname}:7070`;

export default function WelcomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [shop, setShop] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState('');
  const [renameError, setRenameError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resendState, setResendState] = useState('idle');

  useEffect(() => {
    if (!token) { navigate('/admin/login', { replace: true }); return; }
    fetch(`${API_BASE}/api/v1/shops`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error('Failed to load shop');
        return r.json();
      })
      .then((shops) => {
        if (!shops || shops.length === 0) {
          setLoadError(t('welcome.noShopError'));
          return;
        }
        setShop(shops[0]);
        setNewName(shops[0].name || '');
      })
      .catch(() => setLoadError(t('welcome.loadError')));
  }, [token, navigate, t]);

  const customerJoinUrl = shop ? `${window.location.origin}/q/${shop.id}` : '';
  const qrImgSrc = useMemo(() => {
    if (!shop || !token) return null;
    // Use the authenticated backend endpoint via blob so the PNG can carry the Bearer token.
    return `${API_BASE}/api/v1/shops/${shop.id}/qr-code`;
  }, [shop, token]);

  const [qrBlobUrl, setQrBlobUrl] = useState(null);
  useEffect(() => {
    if (!qrImgSrc || !token) return undefined;
    let cancelled = false;
    let objectUrl = null;
    fetch(qrImgSrc, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.blob() : Promise.reject())
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setQrBlobUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [qrImgSrc, token]);

  const handleRename = async (e) => {
    e.preventDefault();
    if (!shop) return;
    const trimmed = newName.trim();
    if (!trimmed) { setRenameError(t('welcome.renameEmpty')); return; }
    setSaving(true);
    setRenameError(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/shops/${shop.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...shop, name: trimmed }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setRenameError(body.message || t('welcome.renameError'));
        return;
      }
      const updated = await res.json();
      setShop(updated);
      setRenaming(false);
    } catch {
      setRenameError(t('welcome.networkError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = () => {
    if (!qrBlobUrl) return;
    const a = document.createElement('a');
    a.href = qrBlobUrl;
    a.download = `vqueue-${(shop?.name || 'office').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-qr.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleResend = async () => {
    setResendState('sending');
    try {
      const res = await fetch(`${API_BASE}/api/v1/auth/resend-verification`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setResendState(res.ok ? 'sent' : 'error');
    } catch {
      setResendState('error');
    }
  };

  if (loadError) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <p style={{ color: '#F87171' }}>{loadError}</p>
          <Link to="/admin/queue" style={s.secondaryLink}>{t('welcome.goToDashboard')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.card}>
        <Link to="/" style={{ textDecoration: 'none', display: 'block', textAlign: 'center', marginBottom: '1rem' }}>
          <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#7C3AED' }}>vQueue</span>
        </Link>

        <h1 style={s.title}>{t('welcome.title')}</h1>
        <p style={s.subtitle}>{t('welcome.subtitle')}</p>

        <div style={s.qrWrap}>
          {qrBlobUrl ? (
            <img src={qrBlobUrl} alt="Queue QR code" width={260} height={260} style={{ borderRadius: 12, display: 'block' }} />
          ) : (
            <div style={{ width: 260, height: 260, background: '#0F172A', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B' }}>
              {t('common.loading')}
            </div>
          )}
        </div>

        <p style={s.tryHint}>{t('welcome.tryHint')}</p>
        <code style={s.urlBox}>{customerJoinUrl}</code>

        <div style={s.office}>
          {renaming ? (
            <form onSubmit={handleRename} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                maxLength={100}
                style={s.input}
              />
              <button type="submit" style={s.btnPrimarySmall} disabled={saving}>
                {saving ? t('welcome.saving') : t('welcome.save')}
              </button>
              <button type="button" style={s.btnSecondarySmall} onClick={() => { setRenaming(false); setNewName(shop?.name || ''); setRenameError(null); }}>
                {t('welcome.cancel')}
              </button>
              {renameError && <p style={{ color: '#F87171', fontSize: '0.85rem', width: '100%', margin: 0 }}>{renameError}</p>}
            </form>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between' }}>
              <div>
                <div style={s.officeLabel}>{t('welcome.officeLabel')}</div>
                <div style={s.officeName}>{shop?.name || '…'}</div>
              </div>
              <button type="button" onClick={() => setRenaming(true)} style={s.btnSecondarySmall}>
                {t('welcome.rename')}
              </button>
            </div>
          )}
        </div>

        <div style={s.actions}>
          <button type="button" onClick={handleDownload} disabled={!qrBlobUrl} style={s.btnPrimary}>
            {t('welcome.downloadQr')}
          </button>
          <button type="button" onClick={() => navigate('/admin/queue')} style={s.btnSecondary}>
            {t('welcome.openDashboard')}
          </button>
        </div>

        {user && !user.emailVerified && (
          <div style={s.verifyBanner}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{t('welcome.verifyTitle')}</div>
            <div style={{ color: '#CBD5E1', fontSize: '0.85rem', lineHeight: 1.5 }}>
              {t('welcome.verifyBody')}
            </div>
            <button type="button" onClick={handleResend} disabled={resendState === 'sending' || resendState === 'sent'} style={s.resendBtn}>
              {resendState === 'sent' ? t('welcome.verifyResent')
                : resendState === 'sending' ? t('welcome.verifyResending')
                : t('welcome.verifyResend')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#0F172A', fontFamily: 'system-ui, sans-serif', padding: '2rem 1rem',
  },
  card: {
    background: '#1E293B', borderRadius: 16, padding: '2rem', width: '100%', maxWidth: 480,
    boxShadow: '0 4px 32px rgba(0,0,0,0.25)', border: '1px solid #334155',
  },
  title: {
    margin: '0 0 0.5rem', fontSize: '1.75rem', fontWeight: 800, color: '#F8FAFC',
    textAlign: 'center', letterSpacing: '-0.02em',
  },
  subtitle: {
    margin: '0 0 1.5rem', fontSize: '0.95rem', color: '#CBD5E1', textAlign: 'center', lineHeight: 1.5,
  },
  qrWrap: {
    display: 'flex', justifyContent: 'center', padding: '0.75rem',
    background: '#fff', borderRadius: 14, margin: '0 auto 1rem', width: 'fit-content',
  },
  tryHint: {
    textAlign: 'center', color: '#94A3B8', fontSize: '0.85rem', margin: '0 0 0.25rem',
  },
  urlBox: {
    display: 'block', background: '#0F172A', border: '1px solid #334155',
    borderRadius: 8, padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: '#CBD5E1',
    wordBreak: 'break-all', marginBottom: '1.5rem', textAlign: 'center',
  },
  office: {
    background: '#0F172A', border: '1px solid #334155', borderRadius: 10,
    padding: '0.85rem 1rem', marginBottom: '1rem',
  },
  officeLabel: {
    fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2,
  },
  officeName: { fontSize: '1rem', fontWeight: 700, color: '#F8FAFC' },
  actions: { display: 'flex', flexDirection: 'column', gap: 8 },
  btnPrimary: {
    width: '100%', padding: '0.75rem', borderRadius: 10, border: 'none',
    background: 'linear-gradient(135deg, #7C3AED, #06B6D4)', color: '#fff',
    fontSize: '1rem', fontWeight: 700, cursor: 'pointer',
  },
  btnSecondary: {
    width: '100%', padding: '0.75rem', borderRadius: 10, border: '1px solid #334155',
    background: 'transparent', color: '#CBD5E1', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
  },
  btnPrimarySmall: {
    padding: '0.5rem 0.85rem', borderRadius: 8, border: 'none',
    background: '#7C3AED', color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
  },
  btnSecondarySmall: {
    padding: '0.5rem 0.85rem', borderRadius: 8, border: '1px solid #334155',
    background: 'transparent', color: '#CBD5E1', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
  },
  input: {
    flex: 1, minWidth: 180, padding: '0.5rem 0.75rem', borderRadius: 8, border: '1.5px solid #334155',
    background: '#1E293B', color: '#F8FAFC', fontSize: '0.95rem', outline: 'none',
  },
  verifyBanner: {
    marginTop: '1.25rem', padding: '0.85rem 1rem',
    background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.35)',
    borderRadius: 10, color: '#FDE68A',
  },
  resendBtn: {
    marginTop: 10, background: 'transparent', border: '1px solid rgba(245,158,11,0.5)',
    color: '#FDE68A', padding: '0.4rem 0.85rem', borderRadius: 8, fontWeight: 600,
    fontSize: '0.85rem', cursor: 'pointer',
  },
  secondaryLink: { color: '#7C3AED', textDecoration: 'none', fontWeight: 600 },
};
